import { useMemo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack } from "@mui/material"
import fp from "lodash/fp"

import type { Device } from "types/device.types"
import type { DeviceGroupRef } from "types/group.types"

import usePermissions from "helpers/hooks/usePermissions"
import usePowerline from "helpers/hooks/powerlines/usePowerline"
import { useAllPowerlines } from "helpers/hooks/powerlines/useAllPowerlines"
import useContextDevices from "helpers/hooks/devices/useContextDevices"
import useInstalledOnRef from "helpers/hooks/devices/useInstalledOnRef"
import Spinner from "widgets/common/Spinner"
import GroupChips from "widgets/deviceGroups/GroupChips"
import PageTitle from "widgets/common/PageTitle"
import EntityTag from "widgets/common/EntityTag"
import AssetContextBlock from "widgets/common/AssetContextBlock"
import MetaItem, { MetaList } from "widgets/common/MetaItem"
import EditableDeviceName from "./editor/EditableDeviceName"

interface DeviceSummaryProps {
  device: Device
  groups: DeviceGroupRef[]
  /** The page's controls, such as the date range. */
  actions?: ReactNode
}

const DeviceSummary = ({ device, groups, actions }: DeviceSummaryProps) => {
  const { isEditor } = usePermissions()
  const { t } = useTranslation()

  const installationType = device.settings?.installation_type
  const powerlineId = device.settings?.powerline_id
  const towerId = device.settings?.tower_id

  // A tower device belongs to a line too, so the header names it either way.
  const { powerline, isLoading: powerlineLoading } = usePowerline({ id: powerlineId })
  // The asset the device is clamped to. The line is the other half.
  const { ref: parentRef, isLoading: parentLoading } = useInstalledOnRef(device.settings)

  // Only a tower needs every line, to find the ones that list it.
  const {
    powerlinesById,
    powerlineIdsByTowerId,
    isLoading: towerLinesLoading,
  } = useAllPowerlines({ skip: installationType !== "tower" })

  // A tower can sit on several lines. The device's own line goes first.
  const towerLines = useMemo(() => {
    if (installationType !== "tower" || !towerId) return []
    const ids = fp.sortBy(
      (id: number) => (id === powerlineId ? 0 : 1),
      powerlineIdsByTowerId[towerId] ?? [],
    )
    return fp.compact(ids.map((id) => powerlinesById[id]))
  }, [installationType, towerId, powerlineId, powerlineIdsByTowerId, powerlinesById])
  const powerLines =
    towerLines.length > 0
      ? towerLines
      : powerline && (installationType === "powerline" || installationType === "tower")
        ? [powerline]
        : []
  const { devices: siblings, isLoading: siblingsLoading } = useContextDevices({
    asset: parentRef,
    excludeId: device.less_id,
  })

  return (
    <Box
      sx={{
        marginBottom: "1rem",
      }}
      data-testid="device-summary"
    >
      <Stack
        flexDirection={"row"}
        columnGap={"15px"}
        rowGap={"5px"}
        alignItems={"center"}
        marginBottom={1.5}
        flexWrap={"wrap"}
      >
        <EntityTag entity="device" />
        {isEditor ? (
          <EditableDeviceName name={device.name} />
        ) : (
          <PageTitle text={device.name} />
        )}
        {/* The end of the title row on desktop, a full-width row of its own on mobile. */}
        {actions && (
          <Box sx={{ marginLeft: { md: "auto" }, width: { xs: "100%", md: "auto" } }}>
            {actions}
          </Box>
        )}
      </Stack>
      {device ? (
        <Stack gap={1.5}>
          <MetaList>
            <MetaItem label="ID">{device.less_id}</MetaItem>
            <MetaItem label={t("device_information.VERSION")}>
              {device.settings?.device_version ?? "-"}
            </MetaItem>
          </MetaList>
          {groups.length !== 0 && <GroupChips groups={groups} />}
          <AssetContextBlock
            powerLines={powerLines}
            location={
              parentRef && {
                label: t(
                  parentRef.entity === "tower"
                    ? "asset_context.INSTALLED_ON_TOWER"
                    : "asset_context.INSTALLED_ON_SPAN",
                ),
                refs: [parentRef],
              }
            }
            devices={parentRef && { items: siblings, siblingsOf: parentRef.entity }}
            loading={{
              powerLines:
                installationType === "tower" ? towerLinesLoading : powerlineLoading,
              location: parentLoading,
              devices: parentLoading || siblingsLoading,
            }}
          />
        </Stack>
      ) : (
        <Spinner />
      )}
    </Box>
  )
}

export default DeviceSummary
